import {
    AlertTriangle,
    CheckCircle2,
    ChevronLeft,
    Copy,
    Info,
    LoaderCircle,
    Sparkles,
    Tag,
    Users,
} from 'lucide-react'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './DuplicateIncentiveTemplateScreen.module.css'

const TYPE_META = {
    SERVICE_COMMISSION: { label: 'Service %', color: '#1565c0', bg: '#e3f2fd' },
    RETAIL_COMMISSION: { label: 'Retail %', color: '#00695c', bg: '#e0f2f1' },
    CATEGORY_COMMISSION: { label: 'By Category', color: '#6a1b9a', bg: '#f3e5f5' },
    SLAB_COMMISSION: { label: 'Tiered Slabs', color: '#e65100', bg: '#fff3e0' },
    TARGET_BONUS: { label: 'Target Bonus', color: '#2e7d32', bg: '#e8f5e9' },
    ATTENDANCE_BONUS: { label: 'Attendance Bonus', color: '#283593', bg: '#e8eaf6' },
    CUSTOM_INCENTIVE: { label: 'Custom', color: '#546e7a', bg: '#eceff1' },
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const DuplicateIncentiveTemplateScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { templateIdentifier: sourceTemplateIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const sourceTemplateName = location.state?.sourceTemplateName || location.state?.templateName || 'Commission Plan'
    const previewStats = useMemo(
        () => ({
            componentCount: Number(location.state?.previewStats?.componentCount || 0),
            slabCount: Number(location.state?.previewStats?.slabCount || 0),
            categoryRateCount: Number(location.state?.previewStats?.categoryRateCount || 0),
            componentTypes: Array.isArray(location.state?.previewStats?.componentTypes)
                ? location.state.previewStats.componentTypes
                : [],
        }),
        [location.state]
    )

    const [newName, setNewName] = useState(`Copy of ${sourceTemplateName}`)
    const [nameError, setNameError] = useState(null)
    const [isSaving, setIsSaving] = useState(false)
    const clearErrorTimerRef = useRef(null)

    useEffect(() => {
        return () => {
            if (clearErrorTimerRef.current) clearTimeout(clearErrorTimerRef.current)
        }
    }, [])

    const duplicateIncentiveTemplate = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) return

        if (!newName.trim()) {
            setNameError('Plan name is required.')
            return
        }
        if (newName.trim().length > 200) {
            setNameError('Plan name must not exceed 200 characters.')
            return
        }
        setNameError(null)

        try {
            setIsSaving(true)

            const response = await fetch(BASE_URL_API + 'duplicateIncentiveTemplate', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    sourceTemplateIdentifier,
                    newTemplateName: newName.trim(),
                    createdBy: authCtx?.authIdentifier || '',
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                const {
                    newTemplate,
                    componentsCopied = 0,
                    warnings = [],
                } = respo.response || {}

                warnings?.forEach((w) => {
                    if (w?.message) toast.warning(w.message)
                })

                toast.success(`Plan duplicated with ${componentsCopied} component(s).`)

                if (newTemplate?.templateIdentifier) {
                    navigate(`/payroll/incentive-structure/${newTemplate.templateIdentifier}`, {
                        replace: true,
                        state: { templateName: newTemplate.templateName || newName.trim() },
                    })
                }
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_004':
                    setNameError(`"${newName.trim()}" already exists.`)
                    if (respo.response?.suggestedName) {
                        setNewName(respo.response.suggestedName)
                        if (clearErrorTimerRef.current) clearTimeout(clearErrorTimerRef.current)
                        clearErrorTimerRef.current = setTimeout(() => setNameError(null), 1500)
                    }
                    break

                case 'VAL_001':
                    if ((respo?.message || '').includes('createdBy')) {
                        toast.error('Session error. Please log out and back in.')
                    } else {
                        setNameError(respo?.message || 'Plan name is required.')
                    }
                    break

                case 'VAL_002':
                    setNameError(respo?.message || 'Plan name must not exceed 200 characters.')
                    break

                case 'COMP_004':
                    toast.error(respo?.message || 'Source template not found.')
                    navigate(-1)
                    break

                case 'SYS_002':
                    toast.error(respo?.message || 'Failed to duplicate plan due to a transaction issue.')
                    break

                default:
                    toast.error(respo?.message || 'Failed to duplicate plan.')
                    break
            }
        } catch (error) {
            console.error('duplicateIncentiveTemplate error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    const charCount = newName.length

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={12} /> Duplicate Commission Plan</span>
                    <h1 className={styles.headerTitle}>Copying from: {sourceTemplateName}</h1>
                </div>
            </header>

            <div className={styles.formWrap}>
                <section className={styles.previewCard}>
                    <div className={styles.previewHeader}>
                        <CheckCircle2 size={14} className={styles.previewHeaderIcon} />
                        <span>
                            {previewStats.componentCount} component{previewStats.componentCount !== 1 ? 's' : ''} will be copied
                        </span>
                    </div>

                    {previewStats.componentTypes.length > 0 && (
                        <div className={styles.typeChipRow}>
                            {previewStats.componentTypes.map((type) => {
                                const meta = TYPE_META[type] || { label: type, color: '#546e7a', bg: '#eceff1' }
                                return (
                                    <span
                                        key={type}
                                        className={styles.typeChip}
                                        style={{ color: meta.color, background: meta.bg }}
                                    >
                                        <Tag size={11} /> {meta.label}
                                    </span>
                                )
                            })}
                        </div>
                    )}

                    {(previewStats.slabCount > 0 || previewStats.categoryRateCount > 0) && (
                        <p className={styles.subCountText}>
                            {previewStats.slabCount > 0 ? `${previewStats.slabCount} slab tier${previewStats.slabCount !== 1 ? 's' : ''}` : ''}
                            {previewStats.slabCount > 0 && previewStats.categoryRateCount > 0 ? ' · ' : ''}
                            {previewStats.categoryRateCount > 0 ? `${previewStats.categoryRateCount} category rate${previewStats.categoryRateCount !== 1 ? 's' : ''}` : ''}
                            {' '}will also be copied
                        </p>
                    )}
                </section>

                <section className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor="newPlanName">
                        New Plan Name <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="newPlanName"
                        className={`${styles.textInput} ${nameError ? styles.textInputError : ''}`}
                        type="text"
                        maxLength={200}
                        value={newName}
                        onChange={(event) => {
                            setNewName(event.target.value)
                            if (nameError) setNameError(null)
                        }}
                        placeholder="e.g. Mid-Level Plan"
                        autoComplete="off"
                    />
                    {charCount > 150 && <p className={styles.charCount}>{charCount}/200</p>}
                    {nameError && (
                        <p className={styles.fieldError}>
                            <AlertTriangle size={12} /> {nameError}
                        </p>
                    )}
                </section>

                <section className={styles.infoNote}>
                    <Info size={14} className={styles.infoNoteIcon} />
                    <p>
                        Staff assignments are <strong>NOT</strong> copied. The new plan starts with zero assigned staff.
                        Assign staff after creation.
                    </p>
                </section>

                <section className={styles.infoNote}>
                    <Users size={14} className={styles.infoNoteIcon} />
                    <p>
                        Commission rules, slab tiers, and category rates are copied exactly.
                        Edit them after creation to adjust for the new staff tier.
                    </p>
                </section>

                <button
                    type="button"
                    className={`${styles.saveBtn} ${isSaving ? styles.saveBtnDisabled : ''}`}
                    onClick={duplicateIncentiveTemplate}
                    disabled={isSaving}
                >
                    {isSaving ? (
                        <><LoaderCircle size={16} className={styles.spin} /> Duplicating...</>
                    ) : (
                        <><Copy size={16} /> Create Duplicate →</>
                    )}
                </button>
            </div>
        </div>
    )
}

export default DuplicateIncentiveTemplateScreen
